import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Archive, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#2D4A22", "#3B5A2F", "#D4AF37", "#8C6D3F", "#5B7C99", "#8C3A3A", "#4A5C6A", "#6E5B8C"];

export default function Courses() {
    const [courses, setCourses] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [params, setParams] = useSearchParams();

    const emptyForm = { name: "", code: "", professor: "", color: COLORS[0], semester: "", credits: 3, archived: false };
    const [form, setForm] = useState(emptyForm);

    const load = async () => { const { data } = await api.get("/courses"); setCourses(data); };
    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (params.get("new") === "1") { setOpen(true); setEditing(null); setForm(emptyForm); params.delete("new"); setParams(params); }
    }, [params]);

    const save = async () => {
        try {
            if (editing) { await api.patch(`/courses/${editing.id}`, form); toast.success("Course updated"); }
            else { await api.post("/courses", form); toast.success("Course created"); }
            setOpen(false); setEditing(null); setForm(emptyForm); load();
        } catch (e) { toast.error("Save failed"); }
    };

    const openEdit = (c) => { setEditing(c); setForm(c); setOpen(true); };

    const remove = async (c) => {
        if (!window.confirm(`Delete ${c.name}? Assignments will be un-linked.`)) return;
        await api.delete(`/courses/${c.id}`); load(); toast.success("Deleted");
    };

    const toggleArchive = async (c) => {
        await api.patch(`/courses/${c.id}`, { ...c, archived: !c.archived }); load();
    };

    const active = courses.filter(c => !c.archived);
    const archived = courses.filter(c => c.archived);

    return (
        <div className="space-y-8" data-testid="courses-page">
            <header className="flex items-end justify-between gap-4">
                <div>
                    <div className="mono-label">Courses</div>
                    <h1 className="font-display text-4xl font-medium tracking-tight mt-2">Your semester</h1>
                    <p className="text-muted-foreground mt-1">Organize your classes, professors, and workload.</p>
                </div>
                <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }} data-testid="new-course-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> New course
                </Button>
            </header>

            {active.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-16 text-center">
                    <div className="font-display text-xl mb-2">No active courses</div>
                    <p className="text-sm text-muted-foreground mb-4">Start by adding the courses you're taking this semester.</p>
                    <Button onClick={() => setOpen(true)}>Add your first course</Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {active.map(c => (
                        <div key={c.id} className="group card-hover bg-card rounded-2xl border border-border/60 overflow-hidden" data-testid={`course-${c.id}`}>
                            <div className="h-2" style={{ background: c.color }} />
                            <div className="p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="mono-label">{c.code || "—"}</span>
                                    <span className="text-xs text-muted-foreground">{c.credits || 3} cr</span>
                                </div>
                                <h3 className="font-display text-lg font-medium leading-tight mb-1">{c.name}</h3>
                                <p className="text-sm text-muted-foreground">{c.professor || "No professor set"}</p>
                                <p className="text-xs text-muted-foreground mt-1">{c.semester || ""}</p>
                                <div className="flex gap-1 mt-4 pt-4 border-t border-border/50 opacity-70 group-hover:opacity-100 transition">
                                    <button className="p-1.5 rounded-md hover:bg-muted" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                    <button className="p-1.5 rounded-md hover:bg-muted" onClick={() => toggleArchive(c)}><Archive className="w-3.5 h-3.5" /></button>
                                    <button className="p-1.5 rounded-md hover:bg-muted text-destructive" onClick={() => remove(c)}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {archived.length > 0 && (
                <section>
                    <div className="mono-label mb-3">Archived</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {archived.map(c => (
                            <div key={c.id} className="bg-muted/30 rounded-xl border border-border/50 p-4 opacity-70">
                                <div className="font-display font-medium">{c.name}</div>
                                <div className="text-xs text-muted-foreground">{c.code}</div>
                                <button className="text-xs text-primary mt-2 hover:underline" onClick={() => toggleArchive(c)}>Restore</button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="course-dialog">
                    <DialogHeader><DialogTitle className="font-display">{editing ? "Edit course" : "New course"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label className="mono-label text-xs">Name</Label><Input data-testid="course-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="mono-label text-xs">Code</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className="mt-1.5" placeholder="CS 101" /></div>
                            <div><Label className="mono-label text-xs">Credits</Label><Input type="number" value={form.credits} onChange={e => setForm({ ...form, credits: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
                        </div>
                        <div><Label className="mono-label text-xs">Professor</Label><Input value={form.professor} onChange={e => setForm({ ...form, professor: e.target.value })} className="mt-1.5" /></div>
                        <div><Label className="mono-label text-xs">Semester</Label><Input value={form.semester} onChange={e => setForm({ ...form, semester: e.target.value })} className="mt-1.5" placeholder="Fall 2026" /></div>
                        <div>
                            <Label className="mono-label text-xs">Accent color</Label>
                            <div className="flex gap-2 mt-2">
                                {COLORS.map(c => (
                                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} data-testid={`color-${c}`}
                                        className={`w-7 h-7 rounded-lg transition ${form.color === c ? "ring-2 ring-offset-2 ring-offset-background ring-primary" : ""}`} style={{ background: c }} />
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-course" disabled={!form.name.trim()}>{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
